/**
 * @(#)2019/8/5.
 * yx组件库基础组件
 * @author 足道
 * Copyright (c) 2019, YUNXI. All rights reserved.
 * YUNXI PROPRIETARY/CONFIDENTIAL. Use is subject to license terms.
 */

import "./index.less";
import "./extra/extraJs";
import { debounce, isFunction, toString } from "lodash";
import config from "../../config";
import React, { Component } from "react";
import { merge, get, toArray, cloneDeep, getQueryString } from "../../utils";
import { withRouter } from "react-router-dom";
import { authParam, showComponent } from "./tools";
import { findDOMNode } from "react-dom";
import { topSendMessage } from "../../utils/iframeMessage/basic";

/**
 * 扩展 Component 组件能力
 *
 * @class YxComponent
 * @extends {Component}
 */
export class YxComponent extends Component {
  /**
   *
   * @param {*} props
   * @param {*} context
   * @param {*} [time=200] 防抖函数周期
   * @memberof YxComponent
   */
  constructor(props, context, time = 200) {
    super(props, context);
    this.history = null;
    this.route = this.props.history;
    this._performanceList = [];
    get(config, "enter", () => {})(this, this.props);
    this.queueStatus = false;
    this.queueInterval = null;
    this._show = this.props._show;
    this._performance = debounce(
      () => {
        // 将会导致组件在 { time } ms内的变化不会即可响应
        if (this._performanceList.length !== 0 && this.queueStatus !== false) {
          this.forceUpdate();
          this._performanceList.pop();
        }
      },
      time,
      { trailing: false, leading: true, maxWait: 3 * time }
    );
  }
  componentDidMount() {
    /* 公共能力为组件获取实例 */
    this.props.onRef && this.props.onRef(this);
  }
  shouldComponentUpdate() {
    let tmpProps = this.props;
    //如果props包含该属性，则阻止渲染
    if (get(tmpProps, "isPreventRendering", false) === true) {
      return false;
    }
    return true;
  }

  /**
   * 将一个state的值状态逆反两次
   *
   * @param {*} key
   * @param {boolean} value
   * @memberof YxComponent
   */
  detourSetState(key, value) {
    this.setState({ [key]: value });
    setTimeout(() => {
      this.setState({ [key]: !value });
    }, 300);
  }

  /**
   * 延时更新state，默认300ms
   *
   * @param {Object} obj
   * @param {number} [time=300]
   * @param {Function} [cbk=undefined]
   * @memberof YxComponent
   */
  asyncSetState(obj, time = 300, cbk = undefined) {
    const stou = setTimeout(() => this.setState(obj), time, cbk);
    return stou;
  }

  /**
   * 开始监听队列，只能在 componentDidMount 执行一次；
   * 实验性功能，如果没有遇到严重的性能问题，不建议使用
   *
   * @param {number} time=50 监听队列时间,默认50毫秒
   * @memberof YxComponent
   */
  _startQueue = (time = 50) => {
    this.queueStatus = true;
    this.queueInterval = setInterval(this._performance, time);
  };

  /**
   * 关闭队列，将会关闭轮询并且失去防抖控制
   *
   * @memberof YxComponent
   */
  _clostQueue = () => {
    this.queueStatus = false;
    clearInterval(this.queueInterval);
  };

  /**
   * 添加到渲染队列,需要在 shouldComponentUpdate 生命周期调用，并且此生命周期返回false；
   * 实验性功能，如果没有遇到严重的性能问题，不建议使用
   *
   * @memberof YxComponent
   */
  _appendQueue = () => {
    if (this.queueStatus === true) {
      this._performanceList.push("");
      return false;
    }
    return true;
  };

  componentWillUnmount() {
    const EventEmitter = get(this, "EventEmitter", null);
    if (EventEmitter) {
      this.EventEmitter.removeAllListeners();
    }
  }

  /**
   * 为了可以快速查看当前组件的数据源
   *
   * @memberof YxComponent
   */
  onContextMenu = (event) => {
    const rightDebug = get(
      this.props,
      "rightDebug",
      config.component.rightDebug
    );
    if (rightDebug === true) {
      event.preventDefault();
      event.stopPropagation();
      const dataList = [
        this.props.sourceData,
        this.state.data,
        this.props.data,
        this.data,
        this.state.dataSource,
        this.props.dataSource,
      ];
      console.log(
        "【\n 本组件的数据源为",
        dataList.find((data) => data),
        "\n 合并多方数据源为",
        merge(...dataList),
        "\n 原形链为",
        this.__proto__,
        "\n】"
      );
      console.log("完整数据集合", {
        "props.sourceData": this.props.sourceData,
        "state.data": this.state.data,
        "props.data": this.props.data,
        "state.dataSource": this.state.dataSource,
        "props.dataSource": this.props.dataSource,
      });
    }
  };

  /**
   * 得到children，增加当为函数的时候，带data渲染
   *
   * @readonly
   * @memberof YxComponent
   */
  get chilldrenJsx() {
    const children = get(this.props, "children", null);
    if (React.isValidElement(children)) {
      return children;
    } else if (children instanceof Function) {
      return children(this.data);
    }
    return null;
  }

  get iocParam() {
    return {
      onContextMenu: this.onContextMenu,
    };
  }

  /**
   * 根据风格类型参数生成className
   *
   * @param {*} tmpProps
   * @param {*} param
   * @returns
   * @memberof YxComponent
   */
  styleParamClassName(tmpProps, param) {
    let className = [get(tmpProps, "className", ""), this.baseClassName || ""];
    for (let classKey in param) {
      const suffixClass = get(tmpProps, param[classKey], "");
      className.push(`${classKey}${suffixClass}`);
    }
    return className.join(" ");
  }

  get parentClass() {
    const className = [get(this.props, "className", "")];
    const childrenClass = toArray(get(this, ["divClassName", "divClass"], ""));
    className.push(...childrenClass);
    return className.join(" ");
  }

  render() {
    return "";
  }
}

export const Route = withRouter;

/**
 * 连接组件，
 * 1.包装一个高阶组件，合并默认props和外部props
 * 2.控制组件显隐
 * 3.注入默认配置
 *
 * @param {*} propsAction
 * @returns
 */
export const Connection = function (propsAction = {}) {
  return function (DOM) {
    return class extends Component {
      addEvent() {
        let { yxEventId } = this.props;
        if (!yxEventId || !getQueryString("isEidt")) return;
        let rootRef = findDOMNode(this);
        rootRef.classList.add(`yx-event-ident`, yxEventId);
        rootRef.addEventListener(
          "click",
          (e) => {
            if (e.target.className.includes(yxEventId)) {
              topSendMessage({ yxEventId });
              if (isFunction(window.__openRenderFn)) {
                window.__openRenderFn(yxEventId);
              }
            }
          },
          false
        );
      }
      componentDidMount() {
        this.addEvent();
      }

      render() {
        let { yxEventId, ...tmpProps } = this.props;
        // let tmpProps = cloneDeep(this.props);
        const auth = authParam(tmpProps);
        let param =
          propsAction instanceof Function ? propsAction() : propsAction;
        const extra = get(auth, "extra", {});
        const showStatus = showComponent(tmpProps, auth);
        // let className = yxEventId ? `yx-event-ident ${yxEventId}` : "";
        return showStatus ? (
          <DOM
            {...merge(param, tmpProps, extra)}
            _show={toString(showStatus)}
          />
        ) : (
          ""
        );
      }
    };
  };
};



// WEBPACK FOOTER //
// ./src/yx-widget/component/YxComponent/index.js